import { Modal } from "staysure-component-library";

import { TextButton } from "@/app/shared-components/cta-button/cta-buttons";

import styleComponent from "./location-to-container.module.css";

type UKRedirectModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalHeaderText: string;
  modalSubText: string;
  confirmButtonText: string;
  cancelButtonText: string;
  onConfirm: () => void;
  onCancel: () => void;
};

function UKRedirectModal({
  isOpen,
  setIsOpen,
  modalHeaderText,
  modalSubText,
  confirmButtonText,
  cancelButtonText,
  onConfirm,
  onCancel,
}: Readonly<UKRedirectModalProps>) {
  const handleConfirmClick = () => {
    setIsOpen(false);
    onConfirm();
  };

  const handleCancelClick = () => {
    setIsOpen(false);
    onCancel();
  };

  // Split text into two paragraphs at "Please note" if present
  const splitText = () => {
    const parts = modalSubText.split("Please note");
    if (parts.length > 1) {
      return {
        firstParagraph: parts[0].trim(),
        secondParagraph: `Please note${parts[1]}`.trim(),
      };
    }
    return {
      firstParagraph: modalSubText,
      secondParagraph: null,
    };
  };

  const { firstParagraph, secondParagraph } = splitText();

  return (
    <Modal
      className={`${styleComponent.same_country_modal} md:p-spacing-xxxl p-spacing-l md:max-w-[416px] max-w-[327px] w-full`}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        if (newIsOpen) {
          setIsOpen(newIsOpen);
        } else {
          // Modal is being closed (backdrop click or ESC key)
          handleCancelClick();
        }
      }}
      underlayStyles={{
        zIndex: 9999,
        background: "rgba(26, 26, 26, 0.6)",
      }}
      modalStyles={{
        display: "flex",
        background: "white",
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: 8,
      }}
    >
      <div data-testid="uk-redirect-modal">
        <h1
          className="text-text-title font-header-font text-[31px] text-left
            font-normal leading-[34px] mb-spacing-s"
          id="uk-redirect-modal-header"
        >
          {modalHeaderText}
        </h1>
        <div
          className="text-text-body font-body-font text-[18px] font-normal
            leading-[27px] mb-spacing-l text-left"
          id="uk-redirect-modal-subtext"
        >
          <p className={secondParagraph ? "mb-spacing-xs" : ""}>
            {firstParagraph}
          </p>
          {secondParagraph && <p>{secondParagraph}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <TextButton
            id="uk-redirect-confirm-button"
            data-testid="uk-redirect-confirm-button"
            hierarchy="primary"
            label={confirmButtonText}
            onClick={handleConfirmClick}
            className="!h-[48px] !px-[24px] !py-[12px] w-full !text-text-functional text-center
              font-body-font !text-[18px] !font-semibold !leading-[18px]
              !rounded-full !bg-interactive-primary-default-1 flex justify-center items-center gap-1"
          />
          <TextButton
            id="uk-redirect-cancel-button"
            data-testid="uk-redirect-cancel-button"
            hierarchy="secondary"
            label={cancelButtonText}
            onClick={handleCancelClick}
            className="!h-[48px] !px-[24px] !py-[12px] w-full !text-border-accent-1-primary text-center
              font-body-font !text-[18px] !font-semibold !leading-[18px]
              !rounded-full !border-[1.5px] !border-border-accent-1-primary flex justify-center items-center gap-1"
          />
        </div>
      </div>
    </Modal>
  );
}

export default UKRedirectModal;
